import {
  FileText,
  MessageSquare,
  Send,
  Loader2
} from 'lucide-react';
import { cn } from '@/lib/utils';
import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { getLanguageDirection } from '../../i18n/config';
import { useAppDispatch, useAppSelector } from '../../redux/store';
import { handleSubmitQueryThunk } from '../../redux/thunks/AILegalMessageThunks';
import { userSelectOneObjectByQuery } from '../../redux/thunks/DBServiceThunks';
import { cleanupStaleProgresses } from '../../redux/slices/legalQueryProgressSlice';
import {
  selectHasActiveProgressForChat as selectSimpleHasActiveProgressForChat,
  cleanupStaleProgresses as cleanupSimpleStaleProgresses
} from '../../redux/slices/simpleLegalProgressSlice';
import { useMQTTConnection } from '../../hooks/useMQTTConnection';
import JurisdictionSelect from '../common/JurisdictionSelect';
import { DEFAULT_JURISDICTION, JurisdictionCode } from '../../common/jurisdictions';
import { Button } from '../ui/button';
import { Textarea } from '../ui/textarea';
import { Card } from '../ui/card';
import { Separator } from '../ui/separator';
import { selectCanEditCurrentCase } from '../../redux/selectors/knowledgebaseSelectors';
import { caseUserSelectByQuery } from '../../redux/thunks/DBServiceThunks';

/**
 * Message input component
 */
const MessageInput: React.FC = () => {
  const { t, i18n } = useTranslation();
  const isRTL = getLanguageDirection(i18n.language) === 'rtl';
  const [message, setMessage] = useState('');
  const dispatch = useAppDispatch();

  const currentCaseId = useAppSelector(state => state.CaseReducer?.currentCaseId);
  const currentChatId = useAppSelector(state => state.ChatReducer.currentChatId);
  const isLoading = useAppSelector(state => state.ChatReducer.isChatLoading);
  const currentUser = useAppSelector(state => userSelectOneObjectByQuery(state.DBUserReducer, {}));
  const currentCase = useAppSelector(state => state.DBCaseReducer.entities?.[state.CaseReducer.currentCaseId as any] as any);

  const defaultJurisdiction: JurisdictionCode = (currentCase?.jurisdiction || currentUser?.jurisdiction || DEFAULT_JURISDICTION) as JurisdictionCode;
  const [jurisdiction, setJurisdiction] = useState<JurisdictionCode>(defaultJurisdiction);

  useEffect(() => {
    setJurisdiction((currentCase?.jurisdiction || currentUser?.jurisdiction || DEFAULT_JURISDICTION) as JurisdictionCode);
  }, [currentCase?.jurisdiction, currentUser?.jurisdiction]);

  const mqttConnection = useMQTTConnection();

  const hasActiveProgress = useAppSelector(state =>
    currentChatId ? selectSimpleHasActiveProgressForChat(state, currentChatId) : false
  );

  // Check if user has edit permission for the current case
  const canEdit = useAppSelector(selectCanEditCurrentCase);

  useEffect(() => {
    const interval = setInterval(() => {
      dispatch(cleanupSimpleStaleProgresses());
      dispatch(cleanupStaleProgresses());
    }, 30000);

    return () => clearInterval(interval);
  }, [dispatch]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (message.trim() && currentChatId && currentCaseId && !isLoading && !hasActiveProgress) {
      try {
        dispatch(handleSubmitQueryThunk({
          chatId: currentChatId,
          query: message,
          caseId: currentCaseId,
          owner: currentUser._id,
          jurisdiction
        }));
        setMessage('');
      } catch (error) {
        console.error('Failed to submit query:', error);
      }
    }
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit(e as any);
    }
  };

  // No case selected
  if (!currentCaseId) {
    return (
      <Card className="w-full flex items-center justify-center p-6 glass border-border/70">
        <div className="flex items-center text-muted-foreground/80">
          <FileText size={20} className="me-3 opacity-70" />
          <p className="text-sm">
            {t('components.messageInput.selectCase')}
          </p>
        </div>
      </Card>
    );
  }

  // No chat selected
  if (!currentChatId) {
    return (
      <Card className="w-full flex items-center justify-center p-6 glass border-border/70">
        <div className="flex items-center text-muted-foreground/80">
          <MessageSquare size={20} className="me-3 opacity-70" />
          <p className="text-sm">
            {t('components.messageInput.selectChat')}
          </p>
        </div>
      </Card>
    );
  }

  const isInputDisabled = isLoading || hasActiveProgress || !canEdit;

  return (
    <div className="w-full">
      <Card
        className={`flex flex-col p-3 glass border-border/70 transition-smooth hover:border-gold/30 focus-within:border-gold focus-within:ring-2 focus-within:ring-gold/20 ${isInputDisabled ? 'opacity-80' : ''}`}
        dir={getLanguageDirection(i18n.language)}
      >
        <form onSubmit={handleSubmit} className="space-y-3">
          <Textarea
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={handleKeyDown}
            placeholder={!canEdit
              ? t('components.messageInput.viewerOnly')
              : hasActiveProgress
                ? t('components.messageInput.processingQuery')
                : isLoading
                  ? t('components.messageInput.loadingConversation')
                  : mqttConnection.isConnected
                    ? t('components.messageInput.placeholderConnected')
                    : t('components.messageInput.placeholder')
            }
            disabled={isInputDisabled}
            className="min-h-[80px] resize-none border-0 focus-visible:ring-0 focus-visible:ring-offset-0 text-[0.95rem] placeholder:text-muted-foreground/50"
            autoFocus={!isInputDisabled}
          />

          <Separator className="opacity-40" />

          <div className="flex items-center justify-between gap-3">
            <JurisdictionSelect
              value={jurisdiction}
              onChange={setJurisdiction}
              label={t('common.jurisdiction.label')}
              size="small"
              inline={true}
            />

            <Button
              type="submit"
              disabled={!message.trim() || isInputDisabled}
              size="sm"
              className={`min-w-[90px] gap-2 ${message.trim() && !isInputDisabled ? 'shadow-md hover:shadow-lg' : ''}`}
            >
              {isInputDisabled ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <>
                  <Send className={cn("w-4 h-4", isRTL && "scale-x-[-1]")} />
                  {t('common.buttons.send')}
                </>
              )}
            </Button>
          </div>
        </form>
      </Card>

      {/* Decorative separator */}
      <div className="flex justify-center mt-2 mx-2 opacity-70">
        <div className="h-0.5 w-20 rounded-full bg-border/60" />
      </div>
    </div>
  );
};

export default MessageInput;
